import m, {MithrilComponent} from '@bitstillery/mithril'
import {classes, pending} from '@bitstillery/common/lib/utils'
import {state} from '@bitstillery/mithril'
import {type ComponentType} from '@bitstillery/mithril'
import {PopupOffer, has_popup_image, type OfferWithPopupImage} from '@/components/popup/offer/offer'
import {PopupPromotion} from '@/components/popup/promotion/promotion'
import {Footer} from '../footer/footer'
import {CMS_POPUP_FRAGMENT, cms_popup_active, dismiss_popup, popup_dismissed_today} from '@/cms/lib/popup'
import {CmsFragment} from '@/cms/runtime/fragments'
import {$s} from '@/app'

export function embed_in_content_frame(Page: ComponentType<unknown>) {
    return class ContentEmbedPage extends MithrilComponent {
        view() {
            return (
                <ContentFrame>
                    <div className='content'>{m(Page)}</div>
                </ContentFrame>
            )
        }
    }
}

/**
 * The content surface below the shell chrome. The hero band and breadcrumbs live in the persistent
 * `ShellHeader` (driven by `$s.page`), not here — that is what lets them transition across page and
 * layout switches instead of remounting with each page.
 */
export class ContentFrame extends MithrilComponent {
    data: {popup_offer: OfferWithPopupImage; popup_promotion: null} = state(
        {
            popup_offer: pending<OfferWithPopupImage>(),
            popup_promotion: null,
        },
        'content-frame',
    )

    popup_offer_active() {
        if (popup_dismissed_today('offer')) {
            return false
        }

        const offers_with_popups = $s.offers.personal
            .filter((offer) => offer.offer_portal_popup_image_s3_key !== null) // Only with an image
            .toSorted((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
            .toReversed()

        for (const offer_to_popup of offers_with_popups) {
            if (has_popup_image(offer_to_popup)) {
                this.data.popup_offer = offer_to_popup
                return true
            }
        }
        return false
    }

    popup() {
        const promotion_popup_active = $s.promotion.active && !popup_dismissed_today('promotion')
        if (promotion_popup_active) {
            return (
                <PopupPromotion
                    onclose={() => {
                        dismiss_popup('promotion')
                        this.data.popup_promotion = null
                    }}
                />
            )
        }
        if (this.popup_offer_active()) {
            return (
                <PopupOffer
                    offer={this.data.popup_offer}
                    onclose={() => {
                        dismiss_popup('offer')
                        this.data.popup_offer = pending()
                    }}
                />
            )
        }
        // Last, so a real offer or the running promotion always wins the one popup slot. The fragment
        // authors its own `Dialog` and closes it through the `popup_dismiss` catalog binding, which is
        // what stores the timestamp `cms_popup_active` reads.
        if (cms_popup_active()) {
            return <CmsFragment name={CMS_POPUP_FRAGMENT} />
        }
        return null
    }

    view(vnode: m.Vnode) {
        // The popups are siblings of the frame, not children of it: `.c-content-frame` carries the
        // page-surface-enter opacity animation (see cms-layout.css), which makes it a stacking
        // context — a modal nested inside it is sealed below the hero band (`z-index: 1`) and the
        // header strip (`100003`) no matter how high its own z-index goes. Hoisting it to the layout
        // column puts `.c-dialog` back in the root stacking context, where its z-index means what it says.
        return [
            <div className={classes('c-content-frame')}>
                <div className='frame-content'>
                    {vnode.children}
                    <Footer />
                </div>
            </div>,
            this.popup(),
        ]
    }
}
